import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiShare2, FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiBookmark, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { postsAPI, commentsAPI, reactionsAPI, favoritesAPI } from '../../services/api';
import { CATEGORIES, REACTIONS } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import CommentSection from '../../components/CommentSection/CommentSection';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './VideoPage.css';

const VideoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const videoRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const [isSaved, setIsSaved] = useState(false);
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReaction, setUserReaction] = useState(null);

  useEffect(() => {
    loadVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadVideo = async () => {
    try {
      const response = await postsAPI.getById(slug);
      setVideo(response.data);
      
      const [commentsRes, reactionsRes, favRes, userReactionRes] = await Promise.all([
        commentsAPI.getByPost(response.data.id),
        reactionsAPI.getByPostId(response.data.id).catch(() => ({ data: {} })),
        isAuthenticated ? favoritesAPI.check(response.data.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isAuthenticated ? reactionsAPI.getUserReaction(response.data.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);
      setComments(commentsRes.data || []);
      setReactionCounts(reactionsRes.data?.reactions || reactionsRes.data?.counts || {});
      setUserReaction(userReactionRes?.data?.userReaction || null);
      setIsSaved(Boolean(favRes?.data?.isFavorite));
    } catch (error) {
      console.error('Error loading video:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (commentData) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para comentar');
      return;
    }
    try {
      await commentsAPI.create({
        postId: video.id,
        content: commentData.text,
        parentId: commentData.parentId
      });
      loadVideo();
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleEditComment = async (commentId, data) => {
    try {
      await commentsAPI.update(commentId, data);
      loadVideo();
    } catch (error) {
      toast.error('Error al editar');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsAPI.delete(commentId);
      loadVideo();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleReaction = async (reactionType) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    try {
      const res = await reactionsAPI.toggle(video.id, reactionType);
      setUserReaction(res.data?.userReaction || null);
      setReactionCounts(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar');
      return;
    }
    try {
      if (isSaved) {
        await favoritesAPI.remove(video.id);
        setIsSaved(false);
        toast.success('Eliminado de guardados');
      } else {
        await favoritesAPI.add(video.id);
        setIsSaved(true);
        toast.success('¡Guardado!');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('No se pudo actualizar guardados');
    }
  };

  const handleEdit = () => {
    navigate(`/posts/edit/${video.id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) return;
    try {
      await postsAPI.deletePost(video.id);
      toast.success('Publicación eliminada');
      navigate('/');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('No se pudo eliminar');
    }
  };

  const handleShare = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para compartir');
      return;
    }
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: video.title, url });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('¡Enlace copiado!');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setProgress((current / total) * 100);
      setCurrentTime(formatTime(current));
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(formatTime(videoRef.current.duration));
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.parentElement.requestFullscreen();
      }
    }
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(cat => cat.id === categoryId) || { name: categoryId, color: '#6366f1' };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="video-page-loading">
        <div className="loading-spinner"></div>
        <p>Cargando video...</p>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="video-page-error">
        <h2>Video no encontrado</h2>
        <Link to="/" className="back-link">Volver al inicio</Link>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(video.category);
  const isOwner = Boolean(user?.id && video?.author?.id && user.id === video.author.id);
  const authorUsername = video?.author?.username || video?.author?.id || video?.author_id;
  const authorIsVerified = Boolean(
    video?.author?.verification_level && video.author.verification_level !== 'none'
  );
  const posterUrl = video?.image ? resolveMediaUrl(video.image) : undefined;

  return (
    <div className="video-page">
      {/* Header */}
      <header className="video-header">
        <Link to="/" className="back-button">
          <FiArrowLeft />
          <span>Volver</span>
        </Link>
      </header>

      <div className="video-container">
        {/* Video Player */}
        <div className="video-player-wrapper">
          <div className="video-player" onClick={togglePlay}>
            <video
              ref={videoRef}
              src={resolveMediaUrl(video.video_url)}
              poster={posterUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            
            {!isPlaying && (
              <div className="play-overlay">
                <FiPlay className="play-icon" />
              </div>
            )}
          </div>

          {/* Video Controls */}
          <div className="video-controls">
            <div className="progress-bar" onClick={handleProgressClick}>
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            
            <div className="controls-row">
              <div className="controls-left">
                <button className="control-btn" onClick={togglePlay}>
                  {isPlaying ? <FiPause /> : <FiPlay />}
                </button>
                <button className="control-btn" onClick={toggleMute}>
                  {isMuted ? <FiVolumeX /> : <FiVolume2 />}
                </button>
                <span className="time-display">
                  {currentTime} / {duration}
                </span>
              </div>
              
              <div className="controls-right">
                <button className="control-btn" onClick={toggleFullscreen}>
                  <FiMaximize />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Video Info */}
        <div className="video-info">
          <span className="video-category" style={{ backgroundColor: categoryInfo.color }}>
            {categoryInfo.name}
          </span>
          
          <h1 className="video-title">{video.title}</h1>
          
          <div className="video-meta">
            <span>{video.views?.toLocaleString() || 0} visualizaciones</span>
            <span>•</span>
            <span>{formatDate(video.created_at)}</span>
          </div>

          {/* Reactions - Solo visible si está autenticado */}
          {isAuthenticated ? (
            <div className="video-reactions-bar">
              {REACTIONS.map(r => (
                <button
                  key={r.id}
                  className={`reaction-btn ${userReaction === r.id ? 'active' : ''}`}
                  onClick={() => handleReaction(r.id)}
                  title={r.label}
                >
                  <span className="reaction-emoji">{r.emoji}</span>
                  <span className="reaction-count">{reactionCounts[r.id] || 0}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="login-prompt-reactions">
              <span>🔒 Inicia sesión para reaccionar</span>
            </div>
          )}

          <div className="video-actions">
            <button className="action-btn" onClick={handleShare}>
              <FiShare2 /> Compartir
            </button>
            <button className={`action-btn ${isSaved ? 'active' : ''}`} onClick={handleSave}>
              <FiBookmark /> Guardar
            </button>
            {isOwner && (
              <>
                <button className="action-btn" onClick={handleEdit}>
                  <FiEdit2 /> Editar
                </button>
                <button className="action-btn" onClick={handleDelete}>
                  <FiTrash2 /> Eliminar
                </button>
              </>
            )}
          </div>

          {/* Author */}
          <div className="video-author">
            <Link to={authorUsername ? `/${authorUsername}` : '/'} className="author-avatar-link">
              <img 
                src={resolveMediaUrl(video.author?.avatar) || `https://i.pravatar.cc/50?u=${video.author_id}`}
                alt={video.author?.name}
                className="author-avatar"
              />
            </Link>
            <div className="author-info">
              <Link to={authorUsername ? `/${authorUsername}` : '/'} className="author-name-link">
                <strong>
                  {video.author?.name}
                  {authorIsVerified && (
                    <MdVerified
                      style={{
                        marginLeft: 6,
                        verticalAlign: 'middle',
                        color: video.author.badge_color || 'currentColor'
                      }}
                      title={video.author.badge_label || 'Cuenta verificada'}
                    />
                  )}
                </strong>
              </Link>
              {video.author?.affiliation && (
                <span>{video.author.affiliation}</span>
              )}
            </div>
          </div>

          {/* Description */}
          {video.description && (
            <div className="video-description">
              <p>{video.description}</p>
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="video-comments">
          <CommentSection
            postId={video.id}
            comments={comments}
            onAddComment={handleAddComment}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
            onRefresh={loadVideo}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
